import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, OnInit } from '@angular/core';
import { NavInformationComponent } from '@components/master-layout/navs/components/nav-information/nav-information.component';
import { AuthenService } from '@core/authen/authen.service';
import { I18nService } from '@core/i18n/i18n.service';
import { PushNotificationService } from '@core/services/push-notification.service';
import { ReportingPopupService } from '@core/services/reporting-popup.service';
import { SessionService } from '@core/session.service';
import { environment } from '@env/environment';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { matPerson } from '@ng-icons/material-icons/baseline';
import {
  matArrowDropDownOutline,
  matLogoutOutline,
} from '@ng-icons/material-icons/outline';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { DefaultAvatarDirective } from '@shared/directives/default-avatar.directive';
import { LanguagePathDirective } from '@shared/directives/language-path.directive';
import { RequireLoginDirective } from '@shared/directives/require-login.directive';
import { UnsavedFormCheckDirective } from '@shared/directives/unsaved-form-check.directive';
import { StorageEnum } from '@shared/enums/storage.enum';
import { NavInterface } from '@shared/interfaces/nav.interface';
import { UserInterface } from '@shared/interfaces/user.interface';
import { FormUtils } from '@shared/utils/form-utils';
import { isMobile } from 'detect-touch-device';

@UntilDestroy()
@Component({
  selector: 'app-nav-profile',
  templateUrl: './nav-profile.component.html',
  imports: [
    NgIcon,
    LanguagePathDirective,
    UnsavedFormCheckDirective,
    CommonModule,
    RequireLoginDirective,
    DefaultAvatarDirective,
    TranslateModule,
    forwardRef(() => NavInformationComponent),
    NgbTooltipModule,
  ],
  viewProviders: [
    provideIcons({ matArrowDropDownOutline, matLogoutOutline, matPerson }),
  ],
})
export class NavProfileComponent implements OnInit {
  profileUrl = environment.jipUrl.profile;
  changePasswordUrl = environment.jipUrl.changePassword;
  changeEmailUrl = environment.jipUrl.changeEmail;
  adminSettingsUrl = environment.jipUrl.adminSettings;

  isMobile = isMobile;

  profile: UserInterface;

  navItem: NavInterface = {
    name: 'Login',
    jipIcon: 'navLogin',
    route: environment.jipUrl.profile,
  };
  tenantUrl: string;

  private readonly pushNotificationService = inject(PushNotificationService);
  private readonly i18Service = inject(I18nService);

  get isHomePage(): boolean {
    const currentLocale = this.i18Service.languagePath;

    return (
      globalThis.location.pathname ===
      `${currentLocale}${environment.jipUrl.home}`
    );
  }

  constructor(
    public authenService: AuthenService,
    protected reportingPopupService: ReportingPopupService,
    public sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.pushNotificationService.listenForNotificationClicks();

    this.authenService
      .getProfile()
      .pipe(untilDestroyed(this))
      .subscribe(userProfile => {
        this.profile = userProfile;

        if (userProfile) {
          void this.pushNotificationService.enableForInstalledApp();
        }
      });

    this.sessionService.apiReady$
      .pipe(untilDestroyed(this))
      .subscribe(ready => {
        if (ready) {
          this.tenantUrl = this.sessionService.getTenantUrl();
        }
      });
  }

  logout(event): void {
    event.preventDefault();
    this.authenService.logout();
  }

  goToProfile(): void {
    if (this.sessionService.authenticated) {
      this.goTo(this.profileUrl);
    }
  }

  goTo(url): void {
    FormUtils.navigateTo(this.sessionService.appendLanguagePath(url));
  }

  openExportPopup(): void {
    this.reportingPopupService.openAdminReport();
  }

  storePreviousUrl(): void {
    localStorage.setItem(StorageEnum.previousUrl, globalThis.location.href);
  }

  goToLoginPage(): void {
    this.goTo(environment.jipUrl.login);
  }

  getCallBackLoginPage(): string {
    const isClientLandingPage = globalThis.location.href.includes(
      environment.jipUrl.clientLandingPage
    );
    const isTermOfUsePage = globalThis.location.href.includes(
      environment.jipUrl.termsOfUse
    );

    if (isClientLandingPage || isTermOfUsePage || this.isHomePage) {
      return environment.jipUrl.news;
    }
  }
}
